import React from "react";
import { Phone, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/controls/customControls/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface AppointmentCardData {
  id: string;
  time: string;
  service: string;
  patientName?: string;
  patientPhone?: string;
  pharmacist?: string;
  duration?: string;
  status: string;
  preConsultationStatus?: "completed" | "outstanding" | null;
}

interface AppointmentCardProps {
  appointment: AppointmentCardData;
  isDarkMode: boolean;
  showPatientInfo?: boolean;
  onCardClick?: (appointment: AppointmentCardData) => void;
  onViewInCalendar?: (appointment: AppointmentCardData) => void;
  onMarkAsNoShow?: (appointment: AppointmentCardData) => void;
  onPreConsultView?: (appointment: AppointmentCardData) => void;
  onPreConsultResend?: (appointment: AppointmentCardData) => void;
}

const STATUS_STYLES: Record<string, { light: string; dark: string }> = {
  pending: {
    light: "border-orange-600 bg-orange-50 text-orange-700",
    dark: "border-orange-500 bg-orange-900/20 text-orange-400",
  },
  overdue: {
    light: "border-red-600 bg-red-50 text-red-700",
    dark: "border-red-500 bg-red-900/20 text-red-400",
  },
  "in progress": {
    light: "border-blue-600 bg-blue-50 text-blue-700",
    dark: "border-blue-500 bg-blue-900/20 text-blue-400",
  },
  active: {
    light: "border-blue-600 bg-blue-50 text-blue-700",
    dark: "border-blue-500 bg-blue-900/20 text-blue-400",
  },
  started: {
    light: "border-blue-600 bg-blue-50 text-blue-700",
    dark: "border-blue-500 bg-blue-900/20 text-blue-400",
  },
  completed: {
    light: "border-green-600 bg-green-50 text-green-700",
    dark: "border-green-500 bg-green-900/20 text-green-400",
  },
  confirmed: {
    light: "border-green-600 bg-green-50 text-green-700",
    dark: "border-green-500 bg-green-900/20 text-green-400",
  },
  "no show": {
    light: "border-purple-600 bg-purple-50 text-purple-700",
    dark: "border-purple-500 bg-purple-900/20 text-purple-400",
  },
  missed: {
    light: "border-purple-600 bg-purple-50 text-purple-700",
    dark: "border-purple-500 bg-purple-900/20 text-purple-400",
  },
  cancelled: {
    light: "border-red-600 bg-red-50 text-red-700",
    dark: "border-red-500 bg-red-900/20 text-red-400",
  },
  removed: {
    light: "border-gray-600 bg-gray-50 text-gray-700",
    dark: "border-gray-500 bg-gray-900/20 text-gray-400",
  },
};

const DEFAULT_STATUS_STYLE = {
  light: "border-gray-600 bg-gray-50 text-gray-700",
  dark: "border-gray-500 bg-gray-900/20 text-gray-400",
};

function getStatusLabel(status: string): string {
  const s = status.toLowerCase().trim();
  if (s === "started" || s === "active") return "In Progress";
  if (s === "missed") return "No Show";
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function getStatusStyle(status: string, isDarkMode: boolean): string {
  const key = status.toLowerCase().trim();
  const styles = STATUS_STYLES[key] || DEFAULT_STATUS_STYLE;
  return isDarkMode ? styles.dark : styles.light;
}

function isNoShowStatus(status: string): boolean {
  const s = status.toLowerCase().trim();
  return s === "no show" || s === "missed";
}

function isTerminalStatus(status: string): boolean {
  const s = status.toLowerCase().trim();
  return s === "completed" || s === "confirmed" || s === "no show" || s === "missed";
}

export function AppointmentCard({
  appointment,
  isDarkMode,
  showPatientInfo = true,
  onCardClick,
  onViewInCalendar,
  onMarkAsNoShow,
  onPreConsultView,
  onPreConsultResend,
}: AppointmentCardProps) {
  const noShow = isNoShowStatus(appointment.status);

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (
      target.tagName === "BUTTON" ||
      target.closest("button") ||
      target.closest('[role="dialog"]')
    ) {
      return;
    }
    onCardClick?.(appointment);
  };

  return (
    <div
      onClick={handleCardClick}
      className={`p-3 rounded-lg cursor-pointer transition-all duration-200 ${
        noShow
          ? isDarkMode
            ? "bg-purple-900/40 hover:bg-purple-900/60"
            : "bg-purple-50 hover:bg-purple-100"
          : isDarkMode
            ? "bg-blue-900/40 hover:bg-blue-900/60"
            : "bg-blue-50 hover:bg-blue-100"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          {/* Time + Service */}
          <div className="mb-1">
            <span
              className={`font-medium transition-colors duration-300 ${
                isDarkMode ? "text-blue-200" : "text-blue-900"
              }`}
            >
              {appointment.time} - {appointment.service}
            </span>
          </div>

          {/* Patient Name */}
          {showPatientInfo && appointment.patientName && (
            <div
              className={`text-sm transition-colors duration-300 ${
                isDarkMode ? "text-blue-300" : "text-blue-700"
              }`}
            >
              Patient: {appointment.patientName}
            </div>
          )}

          {/* Patient Phone */}
          {showPatientInfo && appointment.patientPhone && (
            <div
              className={`flex items-center gap-1 text-sm transition-colors duration-300 ${
                isDarkMode ? "text-blue-300" : "text-blue-700"
              }`}
            >
              <Phone className="w-3 h-3 text-red-600 flex-shrink-0" />
              {appointment.patientPhone}
            </div>
          )}

          {/* Pre-consult Status */}
          {appointment.preConsultationStatus && (
            <div className="flex items-center gap-2 mt-2">
              {appointment.preConsultationStatus === "completed" ? (
                <>
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
                      isDarkMode
                        ? "bg-green-900/30 text-green-300"
                        : "bg-green-100 text-green-800"
                    }`}
                  >
                    <span className="w-2 h-2 bg-green-600 rounded-full" />
                    Pre-consult Complete
                  </span>
                  {onPreConsultView && (
                    <Button
                      size="sm"
                      variant="outline"
                      className={`h-7 text-xs px-2 ${
                        isDarkMode
                          ? "border-green-700 text-green-300 hover:bg-green-900/20"
                          : "border-green-300 text-green-700 hover:bg-green-50"
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onPreConsultView(appointment);
                      }}
                    >
                      View
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
                      isDarkMode
                        ? "bg-red-900/20 text-red-300"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    <span className="w-2 h-2 bg-red-600 rounded-full" />
                    Pre-consult Outstanding
                  </span>
                  {onPreConsultResend && (
                    <Button
                      size="sm"
                      variant="outline"
                      className={`h-7 text-xs px-2 ${
                        isDarkMode
                          ? "border-red-700 text-red-300 hover:bg-red-900/20"
                          : "border-red-300 text-red-700 hover:bg-red-50"
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onPreConsultResend(appointment);
                      }}
                    >
                      Resend
                    </Button>
                  )}
                </>
              )}
            </div>
          )}

          {/* Pharmacist */}
          {appointment.pharmacist && (
            <div
              className={`text-sm mt-1 transition-colors duration-300 ${
                isDarkMode ? "text-blue-300" : "text-blue-700"
              }`}
            >
              Pharmacist: {appointment.pharmacist}
            </div>
          )}
        </div>

        {/* Right Column — Status, Duration, Action Menu */}
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          {/* Status Badge */}
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border ${getStatusStyle(
              appointment.status,
              isDarkMode,
            )}`}
          >
            {getStatusLabel(appointment.status)}
          </span>

          {/* Duration */}
          {appointment.duration && (
            <div
              className={`text-sm transition-colors duration-300 ${
                isDarkMode ? "text-blue-300" : "text-blue-700"
              }`}
            >
              {appointment.duration}
            </div>
          )}

          {/* Action Menu */}
          {!isTerminalStatus(appointment.status) && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-6 w-6 p-0 ${
                    isDarkMode
                      ? "hover:bg-gray-700 text-gray-400"
                      : "hover:bg-gray-200 text-gray-600"
                  }`}
                  onClick={(e) => e.stopPropagation()}
                  title="More options"
                >
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className={`w-44 p-2 ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600"
                    : "bg-white border-gray-200"
                }`}
                align="end"
              >
                <div className="flex flex-col gap-1">
                  {onViewInCalendar && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`justify-start text-xs h-8 ${
                        isDarkMode
                          ? "hover:bg-gray-600 text-gray-200"
                          : "hover:bg-gray-100 text-gray-700"
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewInCalendar(appointment);
                      }}
                    >
                      View in Calendar
                    </Button>
                  )}
                  {onMarkAsNoShow && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`justify-start text-xs h-8 ${
                        isDarkMode
                          ? "text-red-400 hover:bg-gray-600"
                          : "text-red-600 hover:bg-red-50"
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onMarkAsNoShow(appointment);
                      }}
                    >
                      Mark as No Show
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>
    </div>
  );
}

export { getStatusStyle, getStatusLabel, STATUS_STYLES };
